export function formatSomHistoryDate(iso) {
  if (!iso) return 'Unavailable';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unavailable';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatProductionPath(record) {
  const path = record?.execution_path;
  if (path === 'prompt_execution_service') return 'Unified';
  if (path === 'legacy_tavily') return 'Legacy';
  return 'Unavailable';
}

// P0.1: a check is OBSERVED (a real score), INSUFFICIENT_EVIDENCE (too few
// usable answers -- no score) or UNKNOWN (nothing could be checked -- no
// score). Historical checks carry none of these fields and are treated as
// measured, exactly as they always were.
export function checkState(record) {
  const state = record?.measurement_state;
  if (state === 'INSUFFICIENT_EVIDENCE' || state === 'UNKNOWN') return state;
  return 'OBSERVED';
}

export function formatMeasurementState(record) {
  const state = checkState(record);
  if (state === 'INSUFFICIENT_EVIDENCE') return 'Not enough usable answers';
  if (state === 'UNKNOWN') return 'Could not be checked';
  // Historical checks scored failed prompts as "not mentioned". The stored
  // score is left as recorded; we only say so.
  if (record?.score_includes_unknown_prompts) return 'Measured (historical: failed prompts counted as 0)';
  return 'Measured';
}

// Prompts that actually produced an answer. A failed prompt is not "absent".
export function usablePromptCount(record) {
  if (record?.usable_prompts != null) return record.usable_prompts;
  return record?.prompts_tested;
}

export function formatScore(record) {
  const score = record?.score;
  if (checkState(record) !== 'OBSERVED') return 'No score';
  return score == null ? 'Unavailable' : `${score}%`;
}

export function formatMentions(record) {
  const mentions = record?.mentions;
  const usable = usablePromptCount(record);
  const state = checkState(record);
  if (state === 'UNKNOWN') return 'Unavailable';
  if (mentions == null || usable == null) return 'Unavailable';
  return state === 'INSUFFICIENT_EVIDENCE' ? `${mentions}/${usable} (not scored)` : `${mentions}/${usable}`;
}

export function formatUsablePrompts(record) {
  const usable = record?.usable_prompts;
  const tested = record?.prompts_tested;
  if (usable == null || tested == null) return tested == null ? 'Unavailable' : String(tested);
  return `${usable}/${tested} usable`;
}

export function formatMethodology(record) {
  return record?.methodology_hash ? 'Recorded' : 'Not recorded (historical)';
}

export function formatRunStatus(status) {
  if (!status) return 'Unavailable';
  if (status === 'completed' || status === 'success') return 'Completed';
  if (status === 'partial') return 'Partial';
  if (status === 'failed' || status === 'error' || status === 'rejected') return 'Failed';
  return status;
}

// Briefing / Decisions: a failed latest check must never read as all-clear.
// Stale is a display heuristic (7 days), not a product SLA.
export const CHECK_STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

const FAILURE_REASON_COPY = {
  provider_error: 'The AI search provider did not return a usable answer.',
  provider_timeout: 'The AI search provider timed out.',
  prompt_rejected: 'The question was rejected before it was sent.',
  unusable_response: 'The provider returned a response that could not be used.',
  multiple_prompt_failures: 'The questions failed for more than one recorded reason.',
};

export function isCheckStale(record, now = Date.now()) {
  const t = new Date(record?.created_at).getTime();
  if (Number.isNaN(t)) return false;
  return now - t > CHECK_STALE_AFTER_MS;
}

export function latestCheckAttention(summary) {
  const check = summary?.latest_check;
  if (!check) {
    return { kind: summary?.checks ? 'unannotated' : 'absent', check: null };
  }
  const state = checkState(check);
  if (state === 'UNKNOWN') return { kind: 'failed', check };
  if (state === 'INSUFFICIENT_EVIDENCE') return { kind: 'incomplete', check };
  return { kind: 'observed', check };
}

export function latestCheckBlocksAllClear(attention) {
  return attention?.kind === 'failed' || attention?.kind === 'incomplete';
}

export function formatCheckFailureReason(record) {
  if (!record || record.failure_reason_known === false || !record.failure_reason) {
    return 'The precise reason is not recorded.';
  }
  return FAILURE_REASON_COPY[record.failure_reason] || 'The precise reason is not recorded.';
}
